import React, { useMemo } from "react";
import moment from "moment-timezone";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// A table, chart or cards the model chose to show beside its answer (utils/chatVisuals.js on the backend: showTable,
// showChart, showCards), drawn as the agent app draws them.

// One hue for a single series; two more, in a fixed order, when there are several.
const SERIES_COLORS = ["#2a78d6", "#e34948", "#8f8e88"];
const CHART_CHROME = { grid: "#e5e5e5", axis: "#818791" };

const SYDNEY = "Australia/Sydney";

// The model is handed Sydney time as "28 Sep 2026 10:19" and copies it into cells as-is. Parsed back strictly in that
// one format, as Sydney time, and written as the agent app writes dates; a cell that only looks date-ish is left alone.
const displayCell = (cell) => {
  const text = String(cell ?? "");
  const dateTime = moment.tz(text, "D MMM YYYY HH:mm", true, SYDNEY);
  if (dateTime.isValid())
    return dateTime.toDate().toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: SYDNEY,
    });
  const dateOnly = moment.tz(text, "D MMM YYYY", true, SYDNEY);
  if (dateOnly.isValid())
    return dateOnly.toDate().toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: SYDNEY,
    });
  return cell;
};

function PilotTable({ title, columns = [], rows = [] }) {
  if (!columns.length || !rows.length) return null;

  return (
    <div className="overflow-x-auto rounded-xl border border-borderSubtle">
      {title && (
        <p className="border-b border-borderSubtle bg-surfaceMuted px-3 py-1.5 text-xs font-semibold text-textPrimary">
          {title}
        </p>
      )}
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="bg-surfaceMuted">
            {columns.map((column, index) => (
              <th key={index} className="px-3 py-1.5 font-semibold text-textSecondary">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-t border-borderSubtle">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-1.5 text-textPrimary">
                  {displayCell(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// A chart the model chose to show: bar for a comparison across categories, line for a trend over time. The agent app's
// SandyChart in its light theme.
function PilotChart({ chartType, title, categories = [], series = [] }) {
  const data = useMemo(
    () =>
      categories.map((category, index) => {
        const point = { category };
        series.forEach((one) => {
          point[one.name || "Value"] = one.values?.[index] ?? null;
        });
        return point;
      }),
    [categories, series],
  );

  if (!categories.length || !series.length) return null;

  const tick = { fontSize: 11, fill: CHART_CHROME.axis };
  const ChartTag = chartType === "line" ? LineChart : BarChart;

  return (
    <div className="rounded-xl border border-borderSubtle px-3 py-2.5">
      {title && <p className="mb-2 text-xs font-semibold text-textPrimary">{title}</p>}
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <ChartTag data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={CHART_CHROME.grid} />
            <XAxis dataKey="category" tick={tick} tickLine={false} axisLine={false} />
            <YAxis allowDecimals={false} tick={tick} tickLine={false} axisLine={false} width={32} />
            <Tooltip
              cursor={chartType === "line" ? false : { fill: CHART_CHROME.grid, fillOpacity: 0.5 }}
              contentStyle={{ fontSize: 12 }}
            />
            {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
            {series.map((one, index) =>
              chartType === "line" ? (
                <Line
                  key={one.name || index}
                  type="monotone"
                  dataKey={one.name || "Value"}
                  stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ) : (
                <Bar
                  key={one.name || index}
                  dataKey={one.name || "Value"}
                  fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                />
              ),
            )}
          </ChartTag>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Records one by one, each with its own facts and a picture when it has one. No links: the screens a card could open
// belong to the agent app, not this one.
function PilotCards({ title, cards = [] }) {
  if (!cards.length) return null;

  return (
    <div className="space-y-2">
      {title && <p className="text-xs font-semibold text-textPrimary">{title}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {cards.map((card, index) => (
          <div key={index} className="block overflow-hidden rounded-xl border border-borderSubtle bg-surface">
            {card.imageUrl && (
              <img src={card.imageUrl} alt="" className="h-28 w-full rounded-t-xl object-cover" loading="lazy" />
            )}
            <div className="space-y-1 px-3 py-2">
              <p className="text-sm font-semibold text-textPrimary">{card.title}</p>
              {card.subtitle && <p className="text-xs text-textSecondary">{card.subtitle}</p>}
              {card.details?.length > 0 && (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 pt-1 text-xs">
                  {card.details.map((detail, detailIndex) => (
                    <React.Fragment key={detailIndex}>
                      <dt className="text-textMuted">{detail.label}</dt>
                      <dd className="text-textPrimary">{detail.value}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PilotVisual({ visual }) {
  if (visual?.kind === "table") return <PilotTable {...visual} />;
  if (visual?.kind === "chart") return <PilotChart {...visual} />;
  if (visual?.kind === "cards") return <PilotCards {...visual} />;
  return null;
}
